import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, CircularProgress } from "@mui/material";
import CustomModal from "../../common/CustomModal";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import AdsAPIs from "../../../apiRoutes/Ads";
import { GET_DELIVERY_QUERY_KEY } from "../../../utils/constants/queryKeys";
import { ORPHAN_SENTINEL_ID, displayIdOrOrphan } from "../../../utils/constants/ads";
import StatusChip from "../../common/StatusChip";
import QcHistory from "../../common/QcHistory";
import { Button } from "../../ui/button";
import { formatDisplayDate, formatDisplayDateTime } from "../../../utils/helperFunctions";
import { deliveryModerationChipStatus, isDeliveryFinalRejected } from "../../../utils/deliveryModeration";
import usePermissions from "../../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../../utils/constants/permissions";

const Pill = ({ children }) => (
  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-600 border-gray-200">
    {children}
  </span>
);

const LabelValue = ({ label, value, emptyLabel = "—" }) => {
  const isEmpty = !value;
  return (
    <div>
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-sm break-all ${isEmpty ? "text-gray-300 italic" : "text-gray-800"}`}>{isEmpty ? emptyLabel : value}</p>
    </div>
  );
};

// Splits comma-separated value into individual hyperlinks
const LinkList = ({ value, label }) => {
  if (!value) return <span className="text-sm italic text-gray-300">—</span>;
  const parts = value.split(",").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="flex flex-col gap-0.5">
      {parts.map((part, i) => {
        const isUrl = /^https?:\/\//i.test(part);
        return isUrl ? (
          <div key={i} className="flex flex-col gap-0.5">
            <a href={part} target="_blank" rel="noopener noreferrer" className="text-sm text-primaryPurple hover:underline">
              {label && parts.length > 1 ? `${label} ${i + 1}` : label || part}
            </a>
            <span className="text-xs text-gray-400 break-all">{part}</span>
          </div>
        ) : (
          <span key={i} className="text-sm text-gray-800 break-all">{part}</span>
        );
      })}
    </div>
  );
};

const MetaTag = ({ label, value }) => {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
      <span className="font-medium text-gray-400 uppercase tracking-wide">{label}</span>
      <span className="text-gray-600">{value}</span>
    </span>
  );
};

const Divider = () => <div className="border-t border-gray-100 my-4" />;

const SectionTitle = ({ children, className = "" }) => (
  <p className={`text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 ${className}`}>{children}</p>
);

/* ─── main component ─────────────────────────────────────────────────────── */

const AdDeliverDetailModal = ({ open, onClose, deliveryId, onResubmit }) => {
  const axiosPrivate = useAxiosPrivate();
  const { hasPermission } = usePermissions();
  const canCreateDelivery = hasPermission(PERMISSION_CODES.AD_DELIVER_CREATE);
  const { data: detail, isLoading } = useQuery({
    queryKey: [...GET_DELIVERY_QUERY_KEY, deliveryId],
    queryFn: () => AdsAPIs.getDelivery({ axiosPrivate, id: deliveryId }),
    enabled: open && !!deliveryId,
  });

  const req = detail?.ad_request;
  const ad  = detail?.ad;

  const isRejected = detail && (
    detail.moderation_status === "rejected" ||
    detail.finalized_status  === "rejected" ||
    (detail.moderation_errors              && detail.moderation_errors.length)              ||
    (detail.moderation_violation_categories && detail.moderation_violation_categories.length) ||
    (detail.moderation_issues_highlighted  && detail.moderation_issues_highlighted.length)
  );

  // Ad Delivery QC state (gates moderation, so it takes over the status chip).
  const qcStatus = detail?.qc_status || "";
  const qcChip =
    qcStatus === "pending" ? "qc_pending"
    : qcStatus === "changes_requested" ? "qc_changes_requested"
    : qcStatus === "made_live" ? "made_live"
    : null;
  const qcChangesRequested = qcStatus === "changes_requested";
  // QC rounds hang off the request, not the delivery — the verdict is per request.
  const qcHistory = req?.qc_history ?? [];

  const requestedVia = req?.requested_via === "single_entry" ? "Single entry"
    : req?.requested_via === "bulk_entry" ? "Bulk entry"
    : req?.requested_via ?? null;

  // Targeting data — prefer linked ad, fallback to delivery-level fields
  const targetAd = req?.ad ?? ad;

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title="Ad delivery details"
      isButtonsRequired={false}
      className="md:w-[70%] lg:w-[58%] max-h-[90vh]"
    >
      <div
        className="min-h-[200px] pb-4 max-h-[78vh] overflow-y-auto"
        style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
      >
        {!deliveryId ? (
          <p className="text-sm text-gray-500">No delivery selected.</p>
        ) : isLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" py={4} gap={2}>
            <CircularProgress size={24} sx={{ color: "#7f7ad5" }} />
            <span className="text-sm text-gray-600">Loading…</span>
          </Box>
        ) : !detail ? (
          <p className="text-sm text-gray-500">Failed to load details.</p>
        ) : (
          <div>

            {/* Orphan notice */}
            {detail.ad_id === ORPHAN_SENTINEL_ID && detail.ad_request_id === ORPHAN_SENTINEL_ID && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
                Orphan delivery — no linked Ad or Ad request.
              </p>
            )}

            {/* ── 1. HERO STRIP ───────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 mb-4 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex flex-wrap items-center gap-2">
                {detail.ad_id !== ORPHAN_SENTINEL_ID && (
                  <span className="text-sm text-gray-500">
                    ID&nbsp;<span className="font-semibold text-gray-700">#{displayIdOrOrphan(detail.ad_id)}</span>
                  </span>
                )}
                {/* While the delivery is in QC (or was made live from QC), that IS
                    its pipeline state — the moderation chip would read "Pending"
                    when moderation hasn't even seen it yet. */}
                <StatusChip status={qcChip ?? deliveryModerationChipStatus(detail)} size="small" />
                {detail.iteration != null && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                    Iteration {detail.iteration}
                  </span>
                )}
                {detail.finalized_status && (
                  <StatusChip status={detail.finalized_status} size="small" />
                )}
              </div>
              <div className="text-sm text-gray-500">
                {detail.created_on && (
                  <span>Delivered&nbsp;<span className="font-semibold text-gray-700">{formatDisplayDateTime(detail.created_on)}</span></span>
                )}
              </div>
            </div>

            {/* ── 2. TOPIC ────────────────────────────────────────────────── */}
            {detail.topic ? (
              <h3 className="text-base font-semibold text-gray-900 leading-snug mb-1 px-1 break-all">{detail.topic}</h3>
            ) : (
              <p className="text-sm italic text-gray-300 mb-1 px-1">No topic</p>
            )}

            {/* ── 3. TARGETING PILLS ──────────────────────────────────────── */}
            {(targetAd || detail.ad_name) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2 mb-4 px-1">
                {detail.ad_name && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Ad</span>
                    <span className="text-sm text-gray-700 font-medium break-all">{detail.ad_name}</span>
                  </span>
                )}
                {detail.ad_type && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Type</span>
                    <Pill>{detail.ad_type}</Pill>
                  </span>
                )}
                {targetAd?.for_app_name && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">App</span>
                    <Pill>{targetAd.for_app_name}</Pill>
                  </span>
                )}
                {targetAd?.module && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Module</span>
                    <Pill>{targetAd.module}</Pill>
                  </span>
                )}
                {targetAd?.account && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Account</span>
                    <Pill>{targetAd.account}</Pill>
                  </span>
                )}
                {targetAd?.category && (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <span className="font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Category</span>
                    <span className="text-gray-600">{targetAd.category}</span>
                  </span>
                )}
                {targetAd?.track && (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <span className="font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Track</span>
                    <span className="text-gray-600">{targetAd.track}</span>
                  </span>
                )}
              </div>
            )}

            <Divider />

            {/* ── 4. AD ASSET URL ─────────────────────────────────────────── */}
            <div className="mb-4">
              <SectionTitle>Ad asset</SectionTitle>
              {detail.s3_asset_url ? (
                <LinkList value={detail.s3_asset_url} label="Open (S3)" />
              ) : null}
              <LinkList value={detail.ad_asset_url} label={detail.s3_asset_url ? "Open Drive (source)" : "Open link"} />
            </div>

            {/* ── 4b. QC HISTORY ──────────────────────────────────────────── */}
            {/* Every round, not just the current one: qc_feedback on the
                delivery is overwritten each verdict, so the agency could only
                ever see the latest ask. Same component as the requester side. */}
            {(qcHistory.length > 0 || qcChangesRequested) && (
              <div className="mb-4">
                <SectionTitle>Delivery QC</SectionTitle>
                {qcHistory.length > 0 ? (
                  <QcHistory history={qcHistory} />
                ) : (
                  /* No log rows — a round raised before this table existed, or
                     an orphan delivery. Fall back to the current feedback. */
                  <div className="border-l-2 border-amber-400 pl-3">
                    <p className="text-xs font-semibold text-amber-700">
                      Changes requested
                      {detail.qc_at
                        ? ` · ${formatDisplayDateTime(detail.qc_at)}`
                        : ""}
                    </p>
                    <p className="mt-0.5 text-sm text-gray-800 whitespace-pre-wrap">
                      {detail.qc_feedback || (
                        <span className="italic text-gray-400">
                          No feedback recorded.
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {/* No has_resubmission guard, same as the deliveries table: that
                    flag is inferred from a guessed hook line and misfires on
                    unnamed deliveries. qc_status is on this row and is exact. */}
                {qcChangesRequested && onResubmit && canCreateDelivery && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      className="bg-[#7f7ad5] hover:bg-[#6b66c4]"
                      onClick={() => onResubmit(detail)}
                    >
                      Resubmit delivery
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* ── 5. REJECTION FEEDBACK ───────────────────────────────────── */}
            {isRejected && (
              <div className="mb-4 border border-rose-200 bg-rose-50 rounded-lg px-4 py-3 space-y-3">
                <SectionTitle className="text-rose-500">Rejection feedback</SectionTitle>

                {detail.moderation_errors?.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-rose-400 uppercase tracking-wider mb-1">Errors</p>
                    <ul className="space-y-1">
                      {detail.moderation_errors.map((e, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-sm text-rose-800">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0" />
                          {e}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {detail.moderation_violation_categories?.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-rose-400 uppercase tracking-wider mb-1.5">Violation categories</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.moderation_violation_categories.map((v, i) => (
                        <span key={i} className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 border border-rose-200">{v}</span>
                      ))}
                    </div>
                  </div>
                )}

                {detail.moderation_issues_highlighted?.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-rose-400 uppercase tracking-wider mb-1.5">Issues highlighted</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.moderation_issues_highlighted.map((issue, i) => (
                        <span key={i} className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 border border-rose-200">{issue}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resubmit inside rejection card */}
                {onResubmit && canCreateDelivery && isDeliveryFinalRejected(detail) && !detail.has_resubmission && (
                  <div className="pt-2 border-t border-rose-200 mt-1 flex items-center gap-2">
                    <Button type="button" className="bg-[#7f7ad5] hover:bg-[#6b66c4]" onClick={() => onResubmit(detail)}>
                      Resubmit delivery
                    </Button>
                    <div className="relative group">
                      <svg className="w-4 h-4 text-rose-400 cursor-help shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 hidden group-hover:block shadow-lg z-50 leading-relaxed">
                        Create a new delivery for the same ad request and hook. Enter a new video link - iteration is assigned automatically.
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── 6. AD REQUEST META FOOTER ───────────────────────────────── */}
            {req && detail.ad_request_id !== ORPHAN_SENTINEL_ID && req.id !== ORPHAN_SENTINEL_ID && (
              <>
                <Divider />
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-3 px-1">
                  <StatusChip status={req.status === "approved" ? "success" : req.status} size="small" />
                  <StatusChip status={req.priority} size="small" />
                  {req.requested_by_name && <MetaTag label="By"      value={req.requested_by_name} />}
                  {requestedVia           && <MetaTag label="Via"     value={requestedVia} />}
                  {req.due_delivery_date  && <MetaTag label="Due"     value={formatDisplayDate(req.due_delivery_date)} />}
                  {req.created_on         && <MetaTag label="Created" value={formatDisplayDateTime(req.created_on)} />}
                </div>

                {/* Content & references */}
                {(req.ad?.topic ?? req.topic ?? req.comments ?? req.example_reference_link ?? req.bulk_entry_file_url) && (
                  <div className="mb-4 space-y-3">
                    {(req.ad?.topic ?? req.topic) && (req.ad?.topic ?? req.topic) !== detail.topic && (
                      <div>
                        <SectionTitle>Request topic</SectionTitle>
                        <p className="text-sm text-gray-700">{req.ad?.topic ?? req.topic}</p>
                      </div>
                    )}
                    {req.comments && (
                      <div>
                        <SectionTitle>Comments</SectionTitle>
                        <div className="border-l-4 border-purple-200 bg-purple-50/50 pl-3 py-2 pr-3 rounded-r-md text-sm text-gray-700 whitespace-pre-wrap">
                          {req.comments}
                        </div>
                      </div>
                    )}
                    {(req.example_reference_link || req.bulk_entry_file_url) && (
                      <div>
                        <SectionTitle>References</SectionTitle>
                        <div className="space-y-2">
                          {req.example_reference_link && (
                            <div>
                              <p className="text-[11px] text-gray-400 mb-0.5">Example reference</p>
                              <LinkList value={req.example_reference_link} label="Link" />
                            </div>
                          )}
                          {req.bulk_entry_file_url && (
                            <div>
                              <p className="text-[11px] text-gray-400 mb-0.5">Bulk file</p>
                              <LinkList value={req.bulk_entry_file_url} label="File" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── 7. AD DETAILS ───────────────────────────────────────────── */}
            {(targetAd || detail.adset_name) && (
              <>
                <Divider />
                <SectionTitle>Ad details</SectionTitle>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                  <LabelValue label="Ad name"        value={detail.ad_name} />
                  <LabelValue label="Adset name"     value={targetAd?.adset_name || detail.adset_name} />
                  {detail.ad_type && <LabelValue label="Ad type" value={detail.ad_type} />}
                  <LabelValue label="Category"       value={targetAd?.category} />
                  <LabelValue label="Track"          value={targetAd?.track} />
                  <LabelValue label="Track #"        value={targetAd?.track_number ?? detail.track_number} />
                  {targetAd?.created_on && (
                    <LabelValue label="Ad created"   value={formatDisplayDateTime(targetAd.created_on)} />
                  )}
                </div>
              </>
            )}

            {/* ── 8. SPRINT PLAN ──────────────────────────────────────────── */}
            {req?.ad_sprint_plan && (
              <>
                <Divider />
                <SectionTitle>Sprint plan</SectionTitle>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                  <LabelValue label="File name"     value={req.ad_sprint_plan.filename} />
                  <LabelValue label="Delivery date" value={formatDisplayDate(req.ad_sprint_plan.delivery_date)} />
                </div>
                {req.ad_sprint_plan.asset_url && (
                  <div className="mt-3">
                    <p className="text-[11px] text-gray-400 mb-0.5">Asset</p>
                    <LinkList value={req.ad_sprint_plan.asset_url} label="Open asset" />
                  </div>
                )}
              </>
            )}

            {/* Standalone Ad (no linked request) */}
            {ad && detail.ad_id !== ORPHAN_SENTINEL_ID && ad.id !== ORPHAN_SENTINEL_ID && !req?.ad && !targetAd?.category && (
              <>
                <Divider />
                <SectionTitle>Ad</SectionTitle>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                  <LabelValue label="Parent Ad ID"   value={ad.id} />
                  <LabelValue label="Ad name"        value={ad.name} />
                  <LabelValue label="Topic"          value={ad.topic} />
                  <LabelValue label="Adset name"     value={ad.adset_name} />
                  <LabelValue label="Module"         value={ad.module} />
                  <LabelValue label="App"            value={ad.for_app_name} />
                  <LabelValue label="Category"       value={ad.category} />
                  <LabelValue label="Track"          value={ad.track} />
                  {ad.created_on && <LabelValue label="Ad created" value={formatDisplayDateTime(ad.created_on)} />}
                </div>
              </>
            )}

          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default AdDeliverDetailModal;
